// @flow
// ----------------------------------------------------------------------
// <copyright file="Izenda.GoogleMapsDrawingService.js" company="Izenda">
//  Copyright (c) 2015 Izenda, Inc.
//  ALL RIGHTS RESERVED
//
//  The entire contents of this file is protected by U.S. and
//  International Copyright Laws. Unauthorized reproduction,
//  reverse-engineering, and distribution of all or any portion of
//  the code contained in this file is strictly prohibited and may
//  result in severe civil and criminal penalties and will be
//  prosecuted to the maximum extent possible under the law.
//
//  RESTRICTIONS
//
//  THIS SOURCE CODE AND ALL RESULTING INTERMEDIATE FILES
//  ARE CONFIDENTIAL AND PROPRIETARY TRADE
//  SECRETS OF IZENDA INC. THE REGISTERED DEVELOPER IS
//  LICENSED TO DISTRIBUTE THE PRODUCT AND ALL ACCOMPANYING .NET
//  CONTROLS AS PART OF AN EXECUTABLE PROGRAM ONLY.
//
//  THE SOURCE CODE CONTAINED WITHIN THIS FILE AND ALL RELATED
//  FILES OR ANY PORTION OF ITS CONTENTS SHALL AT NO TIME BE
//  COPIED, TRANSFERRED, SOLD, DISTRIBUTED, OR OTHERWISE MADE
//  AVAILABLE TO OTHER INDIVIDUALS WITHOUT EXPRESS WRITTEN CONSENT
//  AND PERMISSION FROM IZENDA INC.
//
//  CONSULT THE END USER LICENSE AGREEMENT(EULA FOR INFORMATION ON
//  ADDITIONAL RESTRICTIONS.
//  </copyright>
// ----------------------------------------------------------------------

import localizer from 'share/localization/Izenda.Localizer';
import { isEmpty, maxBy, isFinite, get, isUndefined, map as mapFn, isNil, find } from 'lodash/fp';
import { isValidLatLong } from './Izenda.GoogleMapsUtils';
import SimpleLegendControl from './Izenda.SimpleLegendControl';
import { GOOGLE_MAPS_CONSTANTS } from 'logic/data/Izenda.ReportPartMapConstants';
import { composeItemKey } from './Izenda.GoogleMapsOptionsBuilder';

const {
  MAX_BUBBLE_SCALE,
  MIN_BUBBLE_SIZE,
  DEFAULT_BUBBLE_STROKE_WEIGHT,
  HIGHLIGHT_BUBBLE_STROKE_WEIGHT,
  BUBBLE_FILL_OPACITY,
  SHADING_STROKE_OPACITY,
  DEFAULT_SHADING_STROKE_WEIGHT,
  HIGHLIGHT_SHADING_STROKE_WEIGHT,
  DEFAULT_SHADING_FILL_OPACITY,
  HIGHLIGHT_SHADING_FILL_OPACITY
} = GOOGLE_MAPS_CONSTANTS;

export default class GoogleMapsDrawingService {
  /**
   * Constructor
   * @param {Object} google
   * @param {google.maps.Map} map
   */
  constructor(google, map) {
    this.google = google;
    this.map = map;
  }

  /**
   * Update map instance
   * @param {Object} google
   * @param {google.maps.Map} map
   * @param {Object} options 
   */
  setMap(google: Object, map: Object, options: Object) {
    this.map = map;
    this.google = google;
    const {changeZoomLevel, changeCenterPoint } = options;
    this.addListener(this.map, 'mousemove', e => (this.mousePositionAsLatLng = e.latLng));
    this.addListener(this.map, 'zoom_changed', () => changeZoomLevel && changeZoomLevel(this.map.getZoom()));
    this.addListener(this.map, 'center_changed', () => changeCenterPoint && changeCenterPoint(this.map.getCenter()));
  }

  /**
   *
   * @param {*} options
   */
  drawBubble(options) {
    const { bubbleSeries } = options;
    const seriesType = 'bubbleSeries';
    if (!isEmpty(bubbleSeries)) {
      const maxBubbleValue = get('max', maxBy('max', bubbleSeries));
      bubbleSeries.forEach(
        series => series.display && this.drawBubbleItem(seriesType, options, maxBubbleValue, series)
      );
    }
  }

  /**
   * Show failed Google Map
   * @param {DOM} chartContainer
   */
  drawFailing(chartContainer) {
    chartContainer.innerHTML = localizer.getText('REPORT_PART_MAP_GOOGLE_MAP_ERROR');
  }

  /**
   * Draw icon when not enough space
   * @param {DOM} chartContainer
   */
  drawIcon(chartContainer) {
    chartContainer.innerHTML = '<i class="icon-chart-bar">';
  }

  /**
   * Draw an information window for marker
   * @param {google.maps.MVCObject} anchor
   * @param {string|DOM} content
   * @param {string} showEvent, default is click event
   */
  drawTooltip(anchor, { isBubble, value }, content, showEvent = 'mouseover', closeEvent = 'mouseout') {
    ensure(!isUndefined(anchor) && !isUndefined(content), 'Anchor and content must be provided.');

    const infoWindow = this.resolveInfoWindow(anchor, { border: true });
    if (!infoWindow) return;

    const drawingService = this;

    const openTooltip = e => {
      // Avoid the shading event override the bubble event
      if (isBubble || !drawingService.isBubbleHover) {
        isBubble && (drawingService.isBubbleHover = true);
        infoWindow.setPosition(e.latLng || this.mousePositionAsLatLng);
        infoWindow.setContent(content);
        infoWindow.open();

        if (!isBubble && this.map.izShowGradientCaret) {
          this.map.izShowGradientCaret(true, value);
        }
      }
    };

    const closeTooltip = () => {
      isBubble && (drawingService.isBubbleHover = false);
      infoWindow.close();
      if (!isBubble && this.map.izShowGradientCaret) {
        this.map.izShowGradientCaret(false);
      }
    };

    function onLabelDraw() {
      drawingService.addDomListener(this._html.wrapper, showEvent, openTooltip);
      drawingService.addDomListener(this._html.wrapper, closeEvent, closeTooltip);
    }

    this.addListener(anchor, showEvent, openTooltip);
    this.addListener(anchor, closeEvent, closeTooltip);

    return onLabelDraw;
  }

  /**
   * Add a listener for an event to an instance.
   * Release listener is released when destroy map automatically.
   * @param {google.maps.Marker} instance
   * @param {string} eventName
   * @param {Function} handler
   */
  addListener(instance, eventName, handler) {
    ensure(!isUndefined(this.map), 'Map must be provided.');

    this.map.izListeners = this.map.izListeners ? this.map.izListeners : [];
    const listener = this.google.maps.event.addListener(instance, eventName, handler);
    this.map.izListeners.push(listener);
  }

  /**
   * Add a listener for an event to an DOM instance.
   * Release listener is released when destroy map automatically.
   * @param {google.maps.Marker} instance
   * @param {string} eventName
   * @param {Function} handler
   */
  addDomListener(instance, eventName, handler) {
    ensure(!isUndefined(this.map), 'Map must be provided.');

    this.map.izListeners = this.map.izListeners ? this.map.izListeners : [];
    const listener = this.google.maps.event.addDomListener(instance, eventName, handler);
    this.map.izListeners.push(listener);
  }

  /**
   * Clear all listeners of map
   */
  clearAllListeners() {
    ensure(!isUndefined(this.map), 'Map must be provided.');
    this.map.izListeners && this.map.izListeners.forEach(l => l && l.remove());
  }

  /**
   * Draw shading
   * @param {Object} options
   */
  drawShading(options: any) {
    ensure(!isUndefined(this.map), 'Map must be provided.');
    const { shadingSeries } = options;
    const seriesType = 'shadingSeries';

    if (!isEmpty(shadingSeries)) {
      shadingSeries.forEach(series => series.display && this.drawBoundary(seriesType, options, series));
    }
  }

  /**
   * Get empty marker icon
   */
  getEmptyMarkerIcon() {
    return {
      scale: 0,
      path: this.google.maps.SymbolPath.CIRCLE
    };
  }

  /**
   * Draw legend control
   * @param {Object} options
   */
  drawLegend(options: any, resolveDrawLegend: Function) {
    const { legend } = options;
    if (!legend || !legend.enabled || isEmpty(legend.series)) {
      resolveDrawLegend && resolveDrawLegend();
      return;
    }
    const controlDiv = document.createElement('div');
    const className = 'izenda-GoogleMaps-LegendWrapper';
    controlDiv.className = className;
    controlDiv.index = 1;
    controlDiv.style.maxWidth = '100%';

    const control = new SimpleLegendControl(this.map, controlDiv, { legend }, resolveDrawLegend);
    const position = this.google.maps.ControlPosition[legend.position];

    this.map.controls[position].push(controlDiv);
    return control.showGradientCaret;
  }

  /* Private methods */
  /**
   * Get Google Maps bubble marker
   * @param {Object} item
   * @param {String} color
   * @param {Number} scalePerValue
   * @param {Number} fillOpacity
   */
  getBubble(item, color, scalePerValue, strokeWeight) {
    return {
      path: this.google.maps.SymbolPath.CIRCLE,
      fillColor: color,
      fillOpacity: BUBBLE_FILL_OPACITY,
      scale: !isFinite(scalePerValue)
        ? MAX_BUBBLE_SCALE
        : Math.max(item.formatedValue * scalePerValue, MIN_BUBBLE_SIZE),
      strokeColor: color,
      strokeWeight: strokeWeight
    };
  }

  /**
   * Draw bubble for a series
   * @param {Object} options
   * @param {Number} maxBubbleValue
   * @param {Object} series
   */
  drawBubbleItem(seriesType, options, maxBubbleValue, { name, data, metricName }) {
    const scalePerValue = MAX_BUBBLE_SCALE / maxBubbleValue;
    const { optionByType } = options;

    const key = composeItemKey([seriesType, name, metricName]);

    const showValueLabel = get('izendaOptions.dataLabels', optionByType);
    const showTooltip = get('tooltip.enabled', optionByType);
    const rotation = get('plotOptions.series.dataLabels.rotation', options);
    const isHoverLabels = get('plotOptions.series.states.hover.enabled', options);
    let items = [];

    data.forEach((point: Object) => {
      if (isValidLatLong(point.lat, point.lon) && !isNil(point.formatedValue)) {
        const latLng = { lat: parseFloat(point.lat), lng: parseFloat(point.lon) };
        const formattedPointName = get('formattedPointName', point);
        const markerIcon = this.getBubble(point, point.color, scalePerValue, DEFAULT_BUBBLE_STROKE_WEIGHT);

        const marker = new this.google.maps.Marker({
          position: latLng,
          icon: markerIcon,
          cursor: 'default',
          map: this.map
        });

        items.push(marker);

        const tooltipCallback =
          showTooltip && this.drawTooltip(marker, { isBubble: true }, point.tooltip, 'mouseover', 'mouseout');

        showValueLabel &&
          this.drawLabel(marker, {
            content: formattedPointName,
            color: point.textColor,
            rotation,
            action: point.action,
            tooltipCallback,
            key,
            seriesType
          });

        if (isHoverLabels) {
          this.addListener(marker, 'mouseover', () =>
            marker.setIcon(this.getBubble(point, point.color, scalePerValue, HIGHLIGHT_BUBBLE_STROKE_WEIGHT))
          );
          this.addListener(marker, 'mouseout', () => marker.setIcon(markerIcon));
        }
      }
    });

    this.map.izItems.set(key, items);
  }

  /**
   * Draw a label for marker
   * @param {google.maps.MVCObject} anchor
   * @param {Object} labelOptions
   */
  drawLabel(anchor, options) {
    ensure(!isUndefined(anchor) && !isUndefined(options), 'Anchor and options must be provided.');

    const labelPosition = anchor.getPosition();

    if (!labelPosition) {
      return;
    }

    const drawingService = this;

    function openLabelCallback() {
      drawingService.addDomListener(this._html.wrapper, 'click', options.action);
      options.tooltipCallback && options.tooltipCallback.apply(this);
    }

    const labelOptions = {
      backgroundColor: 'transparent',
      border: false,
      shadow: false,
      fontColor: options.color,
      isLabel: true,
      padding: '0px',
      rotation: options.rotation,
      isLink: !isNil(options.action),
      callbacks: { open: openLabelCallback }
    };

    const infoWindow = this.resolveInfoWindow(anchor, labelOptions);
    if (!infoWindow) return;

    infoWindow.setContent(options.content);
    infoWindow.open();

    const key = options.key;
    const mapLabels = find({ key }, this.map.izMapLabels);

    if (!mapLabels) {
      this.map.izMapLabels.push({ key: key, display: true, seriesType: options.seriesType, items: [infoWindow] });
    } else {
      mapLabels.items.push(infoWindow);
    }
  }

  /**
   * Get info window of map. Create a new one if needed.
   */
  resolveInfoWindow(marker, options) {
    if (!this.map) return null;
    const SimpleInfoWindow = require('./Izenda.SimpleInfoWindow')(this.google);

    return new SimpleInfoWindow({ map: this.map, marker, ...options });
  }

  /**
   * Draw boundary
   * @param {Object} options
   * @param {Object} series
   */
  drawBoundary(seriesType, options, { name, data, metricName }) {
    const { optionByType } = options;
    const showValueLabel = get('izendaOptions.dataLabels', optionByType);
    const showTooltip = get('tooltip.enabled', optionByType);
    const rotation = get('plotOptions.series.dataLabels.rotation', options);
    const isHoverLabels = get('plotOptions.series.states.hover.enabled', options);
    let items = [];

    const key = composeItemKey([seriesType, name, metricName]);

    data.forEach(area => {
      if (isEmpty(area.pointInfo.coordinates) || isNil(area.formatedValue)) {
        return;
      }
      let coordinates = JSON.parse(area.pointInfo.coordinates);
      coordinates = mapFn(
        coordinate =>
          mapFn(
            c => ({
              lng: Number.parseFloat(c.lng),
              lat: Number.parseFloat(c.lat)
            }),
            coordinate
          ),
        coordinates
      );

      const boundary = new this.google.maps.Polygon({
        paths: coordinates,
        fillColor: area.color,
        strokeColor: area.color,
        strokeOpacity: SHADING_STROKE_OPACITY,
        strokeWeight: DEFAULT_BUBBLE_STROKE_WEIGHT,
        fillOpacity: DEFAULT_SHADING_FILL_OPACITY
      });

      items.push(boundary);

      if (isHoverLabels) {
        this.addListener(boundary, 'mouseover', () =>
          boundary.setOptions({
            fillOpacity: HIGHLIGHT_SHADING_FILL_OPACITY,
            strokeWeight: HIGHLIGHT_SHADING_STROKE_WEIGHT
          })
        );

        this.addListener(boundary, 'mouseout', () =>
          boundary.setOptions({
            fillOpacity: DEFAULT_SHADING_FILL_OPACITY,
            strokeWeight: DEFAULT_SHADING_STROKE_WEIGHT
          })
        );
      }

      let tooltipCallback =
        showTooltip &&
        this.drawTooltip(
          boundary,
          { isBubble: false, value: area.originalValue || area.value },
          area.tooltip,
          'mousemove',
          'mouseout'
        );

      if (showValueLabel && isValidLatLong(area.lat, area.lon)) {
        const latLng = { lat: parseFloat(area.lat), lng: parseFloat(area.lon) };
        const formattedPointName = get('formattedPointName', area);

        const marker = new this.google.maps.Marker({
          position: latLng,
          map: this.map,
          icon: this.getEmptyMarkerIcon(),
          cursor: 'default'
        });

        this.drawLabel(marker, {
          content: formattedPointName,
          color: area.textColor,
          rotation,
          action: area.action,
          tooltipCallback,
          key,
          seriesType
        });
      }
    });

    this.map.izItems.set(key, items);
  }
}



// WEBPACK FOOTER //
// ./ui/share/components/charting/googlemaps/Izenda.GoogleMapsDrawingService.js